/* eslint-disable no-use-before-define */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { CheckIcon, MapPinIcon } from '@heroicons/react/20/solid';
import StopDocumentPreviewModal from 'components/modules/shipments/stops/StopDocumentPreviewModal';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import Alert from 'components/ui/Alert';
import OutgateConfirmationModal from 'components/modules/shipments/OutgateConfirmationModal';

import ShipmentWebAPIUtils, {
  handleStopEquipmentUpdateFail,
  handleStopEquipmentUpdateSuccess,
} from 'utils/ShipmentWebAPIUtils';
import { getLatestStatus, shipperHasTag } from 'utils/formatters/shipments';

import { findNextStop, isStopMissingAppointment } from 'helpers/stops';
import { isShipmentActive } from 'helpers/shipments';

import ShipmentClosedModal from 'components/ui/modals/ShipmentClosedModal';
import { withTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import { containerExists, trailerExists } from 'helpers/equipment';
import { isApac } from 'helpers/helper_functions';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import NextSteps from 'components/modules/shipments/next-steps/NextSteps';
import StopCompleteModal from '../stops/StopCompleteModal';
import StopArrivedModal from '../stops/StopArrivedModal';
import StopEquipmentModal from '../stops/StopEquipmentModal';
import SendBolEmailModal from '../SendBOLEmailModal';
import ShipmentsActionCreators from '../../../../actions/ShipmentsActionCreators';
import SendDOEmailModal from '../SendDOEmailModal';

import AssignDriver from './AssignDriver';
import Cancel from './Cancel';
import GenerateBol from './GenerateBol';
import ShipmentStore from '../../../../stores/ShipmentStore';
import SessionStore from '../../../../stores/SessionStore';
import { isHazMat, getShipperStopDocumentRequirementsFromConfig } from '../../../../helpers/stops';
import ToastStore from '../../../../stores/ToastStore';
import { shipmentStatuses } from '../mappings';
import {
  isDrayageShipment,
  isDrayageOrRailShipment,
  isIntermodalShipmentType,
} from '../../../../helpers/shipment-types';

import { getCircleButtonClass } from '../../../v2/buttons/CircleButton';

import {
  carrierDocTypes,
  DELIVERY_ORDER,
  shipperDocTypes,
} from '../../../../config/documents';
import { Documents } from '../../documents/Documents';
import { InvoicesUploader } from '../../documents/InvoicesUploader';
import MinimumDocumentRequirements from 'components/modules/shipments/MinimumDocumentRequirements';

const confirmationModalDetails = {
  type: 'outgate',
  title: 'ADDITIONAL_STOP_CONFIRM_TITLE',
  message: 'ADDITIONAL_STOP_CONFIRM_MESSAGE',
  confirmButtonText: 'YES',
  hideButtonText: 'NO',
};

const shouldEnterProvideDocumentation = (shipment, stop) => {
  if (stop.complete && stop.missing_docs) {
    return true;
  }
  return false;
};

function getActionName(stop, job, t) {
  if (!stop) {
    return '';
  }

  const { action, empty } = stop;

  // renaming deliver to delivery
  let actionText = action === 'deliver' ? 'DELIVERY' : sluggify(action);
  if (shouldEnterProvideDocumentation(job, stop)) {
    actionText = 'PROVIDE_DOCUMENTATION';
    return actionText;
  }
  if (!empty) {
    return actionText;
  }
  return actionText + '_EMPTY';
}

function sortStops(job) {
  const stops = job.stops || [];

  return _.sortBy(stops, (s) => s.sequence);
}

function getStopsInfo(job, t) {
  const hasDrayageTrailerMovesTag = shipperHasTag(
    job,
    'drayage_trailer_moves',
    job.shipment_type === 'drayage',
  );
  const stops = sortStops(job).map((s) => ({
    ...s,
    completedText:
      s.action === 'pickup'
        ? t('PROOF_OF_PICKUP_COMPLETE')
        : t('PROOF_OF_DELIVERY_COMPLETE'),
    documentationRequired: shouldEnterProvideDocumentation(job, s),
  }));

  const nextStopIndex = stops.findIndex(
    (s) => !s.complete || (s.complete && s.documentationRequired),
  );
  const nextStep = stops[nextStopIndex];
  const actionText = getActionName(nextStep, job, t); // Deliver Empty

  // Add check for delivery empty somewhere here

  const equipments = (nextStep || {}).equipments || [];
  const isDrayageShipment = isIntermodalShipmentType(job);
  const apac = isApac();

  const requireTrailer =
    isDrayageShipment && !trailerExists(equipments || []);
  const requireContainer =
    isDrayageShipment && !containerExists(equipments || []);
  const showAddEquipmentButton = hasDrayageTrailerMovesTag
    ? requireTrailer
    : requireContainer;

  const showNextAction =
    SessionStore.isCarrier() &&
    (!isDrayageShipment ||
      apac ||
      (isDrayageShipment && !apac && equipments.length > 0));
  const requiresArrivalInfo =
    SessionStore.isCarrier() &&
    !apac &&
    job.shipper &&
    job.shipper.requires_arrival_info;

  return {
    stops,
    nextStopIndex,
    nextStep,
    actionText,
    showAddEquipmentButton,
    showNextAction,
    requiresArrivalInfo,
    apac,
  };
}

const isStopCompleteActionDisabled = (shipment, currentStop) => {
  const nextStop = findNextStop(shipment.stops);

  // Always allow prior completed stops to provide documentation
  if (currentStop.missing_docs === true && currentStop.off_site === false) {
    return false;
  }

  // No need to keep completed stops available
  if (currentStop.complete) {
    return true;
  }

  // If not the next stop to complete, then no.
  if (nextStop && nextStop._id !== currentStop._id) {
    return true;
  }

  const isDrayage = isIntermodalShipmentType(shipment);
  // If drayage or rail, we require a container on the stop
  if (isDrayage) {
    if (
      currentStop.equipments.length === 0 ||
      !_.find(currentStop.equipments, { _equipmentType: 'container' })
    ) {
      return true;
    }
  }

  // Require appointments on drayage
  if (isDrayage && isStopMissingAppointment(currentStop)) {
    return true;
  }

  return false;
};

class JobAdmin extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isShipper: false,
      notification: null,
      hideCancel: false,
      hideDocumentUploads: false,
      showStopCompleteModal: false,
      showStopDocumentPreviewModal: false,
      showSendBOLEmailModal: false,
      stopSelected: null,
      showStopEquipmentModal: false,
      showConfirmationModal: false,
      showShipmentClosedModal: false,
      enableShipmentClosedModal: false, // makes sure it only shows the modal once on close
      confirmationModalDetails: {},
      documentsRefetch: null,
      showEmailDeliveryOrderModal: false,
      shipperStopDocumentRequirements: {},
      ...getStopsInfo(props.job || {}, props.t),
    };
  }

  setDocumentsRefetchRef = (documentsRefetch) => {
    this.setState({
      documentsRefetch,
    });
  };

  async componentDidMount() {
    ShipmentStore.addChangeListener(this.shipmentClosedListener);
    ToastStore.addChangeListener(this.handleToastChange);
    this.auditShipmentStatus();
    if (featureEnabled(CONFIGURED_FEATURES.SSR) && isIntermodalShipmentType(this.props.job)) {
      await this.getShipperStopDocRequirements();
    }
  }

  shipmentClosedListener = () => {
    const shipment = ShipmentStore.getShipment(this.props.job._id);
    if (shipment.status === 'job_closed') {
      this.setState({ showShipmentClosedModal: true });
    }
  };

  getShipperStopDocRequirements = async () => {
    const shipperId = this.props.job?.shipper?._id || this.props.job?.shipper;
    this.setState({ shipperStopDocumentRequirements: await getShipperStopDocumentRequirementsFromConfig(shipperId) });
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!_.isEqual(this.props.job, nextProps.job)) {
      this.setState(
        {
          ...getStopsInfo(nextProps.job || {}, nextProps.t),
        },
        () => this.auditShipmentStatus(),
      );
    }
  }

  componentWillUnmount() {
    ToastStore.removeChangeListener(this.handleToastChange);
    ShipmentStore.removeChangeListener(this.shipmentClosedListener);
  }

  renderArrivedButton = (timeline, buttonState, onClick) => {
    const arrivedStatus = 40;
    const { t } = this.props;
    if (timeline.status && timeline.status.code < arrivedStatus) {
      return (
        <button
          className="button button-green"
          disabled={buttonState}
          onClick={onClick}
        >
          {t('ARRIVED')}
        </button>
      );
    }
    return null;
  };

  handleAddEquipment = () => {
    this.setState({
      showStopEquipmentModal: true,
    });
  };

  handleHideEquipmentModal = () => {
    this.setState({
      showStopEquipmentModal: false,
    });
  };

  handleNextStep = () =>
    this.setState((currentState) => {
      const { nextStep } = currentState;

      return {
        stopSelected: nextStep,
        showStopCompleteModal: true,
      };
    });

  handleStopComplete = (shipment, stop, file, meta) => {
    ShipmentsActionCreators.completeStop(shipment, stop, file, meta);
    const {
      shipper: { prompt_for_offsite, tags },
    } = shipment;
    const promptForOutgate =
      prompt_for_offsite || tags.includes('prompt_for_offsite');
    if (SessionStore.isCarrier() && promptForOutgate) {
      this.handleOutgateConfirmationDetails(stop);
    }
    this.setState({ enableShipmentClosedModal: true });
  };

  handleShowDocumentPreview = (stopSelected) => (e) => {
    e.preventDefault();
    this.setState({
      stopSelected,
      showStopDocumentPreviewModal: true,
    });
  };

  handleAssignDriver = ({ driverId }) => {
    const { job: shipment } = this.props;
    ShipmentsActionCreators.assignDriver(shipment, {
      _id: driverId,
    });
  };

  handleToastChange = () => {
    const notification = ToastStore.getLatestNotification();
    if (notification) {
      const { title, level } = notification;

      let hideCancel = false;
      if (level === 'success' && title.includes('Canceled')) {
        hideCancel = true;
      }

      let hideDocumentUploads = false;
      if (level === 'success' && title.includes('Shipment Document')) {
        hideDocumentUploads = true;
      }
      this.setState({
        notification,
        hideCancel,
        hideDocumentUploads,
      });
    }
  };

  handleCloseNotification = () => {
    this.setState({
      notification: null,
    });
  };

  isDrayageShipment() {
    const { job } = this.props;
    return isIntermodalShipmentType(job);
  }

  shouldShowCancel() {
    const {
      job: { canceled },
    } = this.props;
    return !canceled && !this.state.hideCancel;
  }

  canAssignDriver() {
    const { activeDriver } = this.props;

    return activeDriver && activeDriver._id;
  }

  handleCancelShipment = ({ message }) => {
    ShipmentsActionCreators.cancelShipmentForUser(
      this.props.job,
      SessionStore.getUser(),
      message,
    );
  };

  getCompletedStop(stop) {
    let actionText;
    if (stop.action === 'pickup') {
      actionText = 'PROOF_OF_PICKUP_COMPLETE';
    } else {
      actionText = 'PROOF_OF_DELIVERY_COMPLETE';
    }
    const { t } = this.props;
    return (
      <section key={stop._id}>
        <Alert type="success">
          <span>{t(actionText)}</span>
          {stop.proof_of_completion && (
            <a
              className="ml-2"
              href="#"
              onClick={this.handleShowDocumentPreview(stop)}
            >
              ({t('VIEW')})
            </a>
          )}
        </Alert>
        <hr />
      </section>
    );
  }

  handleStopEquipmentUpdateSuccess = (res) => {
    handleStopEquipmentUpdateSuccess(res);
  };

  handleStopEquipmentUpdateFail = (res) => {
    handleStopEquipmentUpdateFail(res);
  };

  handleOutgateConfirmationDetails = (stop) => {
    if (isDrayageShipment(this.props.job) && stop.action === 'pickup') {
      this.setState(
        {
          confirmationModalDetails: {
            ...confirmationModalDetails,
            stop,
          },
        },
        () => {
          this.setState({ showConfirmationModal: true });
        },
      );
    }
  };

  handleOutgateConfirmation = () => {
    if (this.state.confirmationModalDetails.type === 'outgate') {
      this.setState({
        showConfirmationModal: false,
        showOutgateConfirmationModal: true,
      });
    }
  };

  handleOutgateStopCreation = (payload) => {
    this.setState(
      {
        showOutgateConfirmationModal: false,
      },
      async () => {
        await ShipmentWebAPIUtils.createOutgateShipmentStops(
          this.props.job._id,
          payload,
        );
      },
    );
  };

  auditShipmentStatus() {
    const { status } = this.props.job;
    const { nextStep } = this.state;
    const { t } = this.props;
    if (status && nextStep) {
      const shipmentStatus = _.find(shipmentStatuses, { slug: status });
      if (shipmentStatus) {
        if (
          (status === 'arrived' || status === 'en_route') &&
          ((nextStep && !nextStep.windowStart) || !nextStep.windowEnd)
        ) {
          this.addError('need_dates', 'PLEASE_ADD_SHIPMENT_DATES');
        }
        if (
          this.isDrayageShipment() &&
          nextStep &&
          !nextStep.windowStart &&
          !nextStep.windowEnd &&
          !nextStep.empty &&
          nextStep.appointment_unconfirmed
        ) {
          this.addError(
            'appointment_unconfirmed',
            'PLEASE_SCHEDULE_APPOINTMENTS',
          );
        }
        if (
          this.isDrayageShipment() &&
          nextStep &&
          nextStep.windowStart &&
          nextStep.windowEnd &&
          !nextStep.empty &&
          nextStep.appointment_unconfirmed
        ) {
          this.addError(
            'appointment_unconfirmed',
            'PLEASE_CONFIRM_APPOINTMENTS',
          );
        }
      }
    }
  }

  sendBolEmail() {
    return (emails, toSend) => {
      ShipmentsActionCreators.processBol(this.props.job._id, emails, toSend);
    };
  }

  renderHazMat() {
    if (!isHazMat(this.props.job)) {
      return null;
    }
    const { t } = this.props;
    return (
      <section className="mb-4">
        <Alert type="error">
          <span>{t('SHIPMENT_HAZARDOUS_MATERIALS')}</span>
        </Alert>
        <hr />
      </section>
    );
  }

  handleSendEmails = (emails) => {
    ShipmentsActionCreators.sendDeliveryOrder(this.props.job._id, emails);
  };

  handleShowEmailDeliveryOrderModal = () => {
    this.setState({
      showEmailDeliveryOrderModal: true,
    });
  };

  handleHideEmailDeliveryOrderModal = () => {
    this.setState({
      showEmailDeliveryOrderModal: false,
    });
  };

  addError(key, error) {
    this.setState((state) => {
      const errors = { ...state.errors, [key]: error };
      return { errors };
    });
  }

  renderErrors() {
    const { errors } = this.state;
    const { t } = this.props;
    if (_.isEmpty(errors)) {
      return null;
    }
    return (
      <Alert type="error" className="mb-4">
        {Object.keys(errors).map((key) => (
          <div key={key}>{t(errors[key])}</div>
        ))}
      </Alert>
    );
  }

  renderStopOffAction = () => {
    const { job, t } = this.props;
    const { nextStep: stop, stops } = this.state;
    const pickupStop = stops.find(
      (stop) => stop.action === 'pickup' && stop.sequence === 1,
    );

    const isStopOffEligible =
      (job && stop && isDrayageShipment(job) && stop.action === 'deliver') ||
      stop.action === 'deliver_empty';
    if (!isStopOffEligible || !pickupStop) {
      return null;
    }

    const handleStopOff = () =>
      this.setState({
        confirmationModalDetails: {
          ...confirmationModalDetails,
          stop: pickupStop,
        },
        showOutgateConfirmationModal: true,
      });

    return (
      <div className="px-2">
        <button
          className="button button-blue"
          disabled={!isStopOffEligible}
          onClick={handleStopOff}
        >
          {t('ADD_STOP_OFF')}
        </button>
      </div>
    );
  };

  renderStopAction = () => {
    const {
      showNextAction,
      nextStep: stop,
      requiresArrivalInfo,
      errors,
      actionText,
      apac,
    } = this.state;
    const { job, t } = this.props;
    const timeline = getLatestStatus(stop);
    const completeActionDisabled = isShipmentActive(job)
      ? isStopCompleteActionDisabled(job, stop)
      : true;
    const { equipments } = stop;
    const hasDrayageTrailerMovesTag = shipperHasTag(
      job,
      'drayage_trailer_moves',
      job.shipment_type === 'drayage',
    );
    const hasTrailer = trailerExists(equipments || []);
    const shouldHaveTrailer = hasDrayageTrailerMovesTag && !hasTrailer;
    const arrivedButtonState =
      completeActionDisabled || (shouldHaveTrailer && !apac);
    const arrivalButton =
      requiresArrivalInfo &&
      this.renderArrivedButton(timeline, arrivedButtonState, () =>
        this.setState({ showStopArrivedModal: true }),
      );
    if (arrivalButton) {
      return arrivalButton;
    }

    if (showNextAction) {
      return (
        <div className="px-2">
          <button
            className="button button-green"
            disabled={shouldHaveTrailer || !_.isEmpty(errors)}
            onClick={this.handleNextStep}
          >
            {`${t('COMPLETE')} ${t(actionText)}`}
          </button>
        </div>
      );
    }

    return null;
  };

  renderLocationMarker(stop) {
    return (
      <div className="flex flex-wrap mb-4 space-x-1 items-center">
        <MapPinIcon className="w-4 h-4 text-green" />
        <span>{stop.location[0].address}</span>
      </div>
    );
  }

  renderSteps() {
    const { job, t } = this.props;
    if (featureEnabled(CONFIGURED_FEATURES.SSR) && this.isDrayageShipment() && !SessionStore.isShipper()) {
      return <NextSteps shipment={job} />;
    }
    return <h3 className="my-0 mb-2">{t('NEXT_STEPS')}</h3>;
  }

  renderMinimumRequiredDocs() {
    const { job, isCarrier } = this.props;
    // getting different reference_numbers._id each time and it re-renders this component
    const {reference_numbers, ...shipment} = job 

    if(isCarrier && isDrayageOrRailShipment(job)) {
      return <MinimumDocumentRequirements shipment={shipment} />
    }

    return null
  }

  renderNextStep() {
    const { nextStep, showAddEquipmentButton } = this.state;

    const { job, t } = this.props;

    if (!nextStep) {
      return (
        <div className="flex mb-4 items-center space-x-2">
          <div
            className={getCircleButtonClass({ color: 'brand', size: 'small' })}
          >
            <CheckIcon className="w-5 h-5" />
          </div>
          <span className="text-sm">{t('SHIPMENT_COMPLETED')}</span>
        </div>
      );
    }

    const promptForOutgate =
      job.shipper.prompt_for_offsite ||
      job.shipper.tags.includes('prompt_for_offsite');

    return (
      <div className="mb-4">
        {this.renderLocationMarker(nextStep)}
        {this.renderSteps()}
        {this.renderErrors()}
        <div className="flex flex-wrap items-center -mx-2">
          {this.shouldShowCancel() && (
            <div className="px-2">
              <Cancel
                job={this.props.job}
                onCancelShipment={this.handleCancelShipment}
              />
            </div>
          )}
          {showAddEquipmentButton && (
            <div className="px-2">
              <button
                className="button button-green"
                onClick={this.handleAddEquipment}
              >
                {`${t('ADD_EQUIPMENT_FOR')} ${t(this.state.actionText)}`}
              </button>
            </div>
          )}
          {promptForOutgate &&
            SessionStore.isCarrier() &&
            this.renderStopOffAction()}
          {!showAddEquipmentButton && this.renderStopAction()}
        </div>
      </div>
    );
  }

  renderNotification() {
    const { notification } = this.state;

    if (!notification) {
      return null;
    }

    let type = '';
    if (notification.level === 'success') {
      type = 'success';
    } else if (notification.level === 'error') {
      type = 'error';
    }

    const { body, title } = notification;

    const text = body || title;

    return (
      <Alert type={type} canClose onClose={this.handleCloseNotification}>
        {text}
      </Alert>
    );
  }

  render() {
    const isDrayageShipment = this.isDrayageShipment();
    const { t, carrierPartner } = this.props;

    let uploadableDocTypes;

    if (this.props.isShipper) {
      uploadableDocTypes = shipperDocTypes;
    } else if (this.props.job.status !== 'job_closed') {
      uploadableDocTypes = carrierDocTypes.filter(
        (docType) => docType.value !== DELIVERY_ORDER.value,
      );
    } else {
      uploadableDocTypes = carrierDocTypes;
    }
    return (
      <div className="p-4">
        {this.renderNotification()}
        {this.renderMinimumRequiredDocs()}
        {!this.props.job.canceled && (
          <section className="mb-4">
            {this.renderNextStep()}
            <hr />
          </section>
        )}
        {this.renderHazMat()}

        {this.props.job.canceled && (
          <Alert type="info">{t('SHIPMENT_CANCELED')}</Alert>
        )}

        {this.props.isShipper && (
          <GenerateBol
            current_status={this.props.job.status}
            isShipper={this.props.isShipper}
            onGenerateBol={() => this.setState({ showSendBOLEmailModal: true })}
          />
        )}

        {this.state.showStopEquipmentModal && (
          <StopEquipmentModal
            show={this.state.showStopEquipmentModal}
            stopId={this.state.nextStep._id}
            shipmentId={this.props.job._id}
            onStopEquipmentUpdateSuccess={this.handleStopEquipmentUpdateSuccess}
            onStopEquipmentUpdateFail={this.handleStopEquipmentUpdateFail}
            onHide={this.handleHideEquipmentModal}
            isCarrier
            carrierPartner={carrierPartner}
          />
        )}

        {this.state.stopSelected && this.state.showStopCompleteModal && (
          <StopCompleteModal
            stopId={this.state.stopSelected._id}
            shipmentId={this.props.job._id}
            show={this.state.showStopCompleteModal}
            onStopComplete={this.handleStopComplete}
            onStopEquipmentUpdateSuccess={this.handleStopEquipmentUpdateSuccess}
            onStopEquipmentUpdateFail={this.handleStopEquipmentUpdateFail}
            onHide={() => {
              this.setState({
                showStopCompleteModal: false,
                stopSelected: false,
              });
              this.state.documentsRefetch?.();
            }}
            shipperStopDocumentRequirements={this.state.shipperStopDocumentRequirements}
            isCarrier
          />
        )}
        {this.state.stopSelected && this.state.showStopDocumentPreviewModal && (
          <StopDocumentPreviewModal
            shipment={this.props.job}
            show={this.state.showStopDocumentPreviewModal}
            stop={this.state.stopSelected}
            onHide={() => {
              this.setState({
                showStopDocumentPreviewModal: false,
                stopSelected: null,
              });
              this.state.documentsRefetch?.();
            }}
          />
        )}

        {this.props.isShipper && (
          <SendBolEmailModal
            shipment={this.props.job}
            show={this.state.showSendBOLEmailModal}
            onSend={this.sendBolEmail()}
            onHide={() => this.setState({ showSendBOLEmailModal: false })}
          />
        )}

        {this.state.showConfirmationModal && (
          <ConfirmModal
            show={this.state.showConfirmationModal}
            title={`${t(this.state.confirmationModalDetails.title)}`}
            message={`${t(this.state.confirmationModalDetails.message)}`}
            affirmative={t(
              this.state.confirmationModalDetails.confirmButtonText,
            )}
            hideButtonText={t(
              this.state.confirmationModalDetails.hideButtonText,
            )}
            onConfirm={this.handleOutgateConfirmation}
            onHide={() => this.setState({ showConfirmationModal: false })}
          />
        )}

        {this.state.showOutgateConfirmationModal && (
          <OutgateConfirmationModal
            show={this.state.showOutgateConfirmationModal}
            onCancel={() =>
              this.setState({
                showOutgateConfirmationModal: false,
              })
            }
            onSave={this.handleOutgateStopCreation}
            stop={this.state.confirmationModalDetails.stop}
          />
        )}

        {this.state.showStopArrivedModal && (
          <StopArrivedModal
            stop={this.state.nextStep}
            shipment={this.props.job}
            show={this.state.showStopArrivedModal}
            onHide={() => this.setState({ showStopArrivedModal: false })}
            onStopArrived={ShipmentsActionCreators.transitionStop}
          />
        )}

        {!this.state.hideDocumentUploads &&
          !this.props.isShipper &&
          this.state.stops.map((stop) => {
            if (stop.complete && !stop.missing_docs) {
              return this.getCompletedStop(stop);
            }

            if (isDrayageShipment) {
              return null;
            }

            return null;
          })}
        {!this.state.isShipper && this.canAssignDriver() && (
          <section>
            <AssignDriver
              selectedDriver={this.props.activeDriver}
              drivers={this.props.drivers}
              isCarrier={!this.props.isShipper}
              onCompleteAssignDriver={this.handleAssignDriver}
            />
          </section>
        )}
        {this.state.enableShipmentClosedModal &&
          this.state.showShipmentClosedModal && (
            <ShipmentClosedModal
              show={this.state.showShipmentClosedModal}
              onAccessorialClick={this.props.goToPayouts}
              onCancelClick={() =>
                this.setState({
                  showShipmentClosedModal: false,
                  enableShipmentClosedModal: false,
                })
              }
            />
          )}
        {this.state.showEmailDeliveryOrderModal && (
          <SendDOEmailModal
            show={this.state.showEmailDeliveryOrderModal}
            onSend={this.handleSendEmails}
            onHide={this.handleHideEmailDeliveryOrderModal}
          />
        )}

        <section>
          <div>
            <h4 className="border-solid border-b border-grey-light">
              {t('ATTACHED_DOCUMENTS_AND_INVOICES')}
            </h4>
            <Documents
              shipment={this.props.job}
              carrierDocumentUpload={!this.props.isShipper}
              docTypes={uploadableDocTypes}
              deleteDisabled
              isOrder={false}
              onEmail={this.handleShowEmailDeliveryOrderModal}
              refetchRef={(documentsRefetch) =>
                this.setDocumentsRefetchRef(documentsRefetch)
              }
            />
          </div>
        </section>
      </div>
    );
  }
}

JobAdmin.propTypes = {
  job: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    shipment_id: PropTypes.string.isRequired,
    canceled: PropTypes.bool.isRequired,
    current_status: PropTypes.string.isRequired,
    signatory: PropTypes.string,
    signatory_pickup: PropTypes.string,
    timeline: PropTypes.object,
    stops: PropTypes.array,
    shipment_type: PropTypes.string.isRequired,
  }).isRequired,
  isShipper: PropTypes.bool.isRequired,
  isCarrier: PropTypes.bool,
  drivers: PropTypes.array,
  activeDriver: PropTypes.shape({
    _id: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
  }),
};

JobAdmin.defaultProps = {
  drivers: [],
  activeDriver: {},
};

export default withTranslation()(JobAdmin);
